import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import AgeGate from "@/components/AgeGate";
import InstallPrompt from "@/components/InstallPrompt";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import NativeCheckPage from "@/pages/NativeCheck";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import FeedPage from "@/pages/Feed";
import LivePage from "@/pages/Live";
import CommunitiesPage from "@/pages/Communities";
import NotificationsPage from "@/pages/Notifications";
import ProfilePage from "@/pages/Profile";
import EditProfilePage from "@/pages/EditProfile";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import NearbyPage from "@/pages/Nearby"; // eslint-disable-line no-unused-vars
import SocialMeetPage from "@/pages/SocialMeet";
import AdminPage from "@/pages/Admin";
import VerifyAgePage from "@/pages/VerifyAge";
import CoinStorePage from "@/pages/CoinStore";
import VipBadgesPage from "@/pages/VipBadges";
import TopBadgeCampaignPage from "@/pages/TopBadgeCampaign";
import PaymentSuccessPage from "@/pages/PaymentSuccess";
import PaymentCancelPage from "@/pages/PaymentCancel";
import InboxPage from "@/pages/Inbox";

import JoinRedirect from "@/pages/JoinRedirect";
import VerifyEmail from "@/pages/VerifyEmail";
import PandaPackPage from "@/pages/PandaPack";
import TutorialPage from "@/pages/Tutorial";
import LiveNow from "@/pages/LiveNow";
import CreatorDashboardPage from "@/pages/CreatorDashboard";
import UnsubscribePage from "@/pages/Unsubscribe";
import EarningsPayoutsPage from "@/pages/EarningsPayouts";
import GiftTradesPage from "@/pages/GiftTrades";
import BlockedUsersPage from "@/pages/BlockedUsers";
import AmbassadorLeaderboard from "@/pages/AmbassadorLeaderboard";
import AmbassadorApply from "@/pages/AmbassadorApply";
import ReferralLanding from "@/pages/ReferralLanding";
import AmbassadorDashboard from "@/pages/AmbassadorDashboard";
import About from "@/pages/About";
import Status from "@/pages/Status";
import FaqCreators from "@/pages/FaqCreators";
import FaqMembers from "@/pages/FaqMembers";
import TermsOfServicePage from "@/pages/legal/TermsOfService";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicy";
import RefundPolicyPage from "@/pages/legal/RefundPolicy";
import SupportPage from "@/pages/Support";
import LegalHubPage from "@/pages/LegalHub";
import AcceptableUsePage from "@/pages/legal/AcceptableUse";
import ChildSafetyPage from "@/pages/legal/ChildSafety";
import CommunityDetail from "@/pages/CommunityDetail";
import CommunityCamRoom from "@/pages/CommunityCamRoom";
import PrivateGroupsPage from "@/pages/PrivateGroups";
import CreatorShopPage from "@/pages/CreatorShop";
import GroupFeedPage from "@/pages/GroupFeed";
import FollowersPage from "@/pages/Followers";
import PageviewTracker from "@/components/PageviewTracker";
import RecapPrompt from "@/components/RecapPrompt";
import { Toaster } from "sonner";

function InnerRouter() {
  const location = useLocation();
  // Handle Google OAuth callback that lands as `#session_id=...`
  if ((location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PageviewTracker />
      <RecapPrompt />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/live-now" element={<LiveNow />} />
      <Route path="/ambassadors" element={<AmbassadorLeaderboard />} />
      <Route path="/ambassadors/apply" element={<AmbassadorApply />} />
      <Route path="/ref/:handle" element={<ReferralLanding />} />
      <Route path="/join" element={<JoinRedirect />} />
      <Route path="/native-check" element={<NativeCheckPage />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/about" element={<About />} />
      <Route path="/status" element={<Status />} />
      <Route path="/faq/creators" element={<FaqCreators />} />
      <Route path="/faq/members" element={<FaqMembers />} />
      <Route path="/legal/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/legal" element={<LegalHubPage />} />
      <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/refund-and-cancellation-policy" element={<RefundPolicyPage />} />
      <Route path="/legal/acceptable-use-and-content-policy" element={<AcceptableUsePage />} />
      <Route path="/legal/child-safety" element={<ChildSafetyPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/live" element={<LivePage />} />
        <Route path="/nearby" element={<SocialMeetPage />} />
        <Route path="/communities" element={<CommunitiesPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/u/:handle" element={<ProfilePage />} />
        <Route path="/u/:handle/edit" element={<EditProfilePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/verify" element={<VerifyAgePage />} />
        <Route path="/creator" element={<CreatorDashboardPage />} />
        <Route path="/earnings" element={<EarningsPayoutsPage />} />
        <Route path="/coins" element={<CoinStorePage />} />
        <Route path="/vip" element={<VipBadgesPage />} />
        <Route path="/top-badge" element={<TopBadgeCampaignPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/members" element={<Navigate to="/nearby?tab=all" replace />} />
        <Route path="/referrals" element={<PandaPackPage />} />
        <Route path="/reels" element={<Navigate to="/nearby?tab=all" replace />} />
        <Route path="/tutorial" element={<TutorialPage />} />
        <Route path="/gift-trades" element={<GiftTradesPage />} />
        <Route path="/blocked" element={<BlockedUsersPage />} />
        <Route path="/private-groups" element={<PrivateGroupsPage />} />
        <Route path="/private-groups/:groupId" element={<GroupFeedPage />} />
        <Route path="/u/:handle/followers" element={<FollowersPage mode="followers" />} />
        <Route path="/u/:handle/following" element={<FollowersPage mode="following" />} />
        <Route path="/creator-shop" element={<CreatorShopPage />} />
        <Route path="/ambassador/dashboard" element={<AmbassadorDashboard />} />
        <Route path="/c/:id" element={<CommunityDetail />} />
        <Route path="/c/:id/cam" element={<CommunityCamRoom />} />
      </Route>

      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AgeGate />
          <InstallPrompt />
          <InnerRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#0a0a0a",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 0,
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
